
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import { Template, CaseDetails, AdvancedSearchFilter, dataTypeByFieldType, SearchClause } from "@/types"
import { PagedData } from "@/types/pagedData"
import { caseColumns, CaseColumn } from "@/types/caseColumns"
import { Operator, operatorLabels, operatorsByFieldType } from "@/types/insights/operators"

import { filterMixin } from "@/mixins/filtering"

import api from "@/services/api"

import ColumnFilters from "@/components/Search/ColumnFilters.vue"
import UserImage from "@/components/UserImage.vue"
import CaseTable from "@/components/CaseList/CaseTable.vue"

const setup = namespace("setup")

const { filterList } = filterMixin.methods

@Component({
	components: {
		"user-image": UserImage,
		"column-filters": ColumnFilters,
		"scrollbar": VuePerfectScrollbar,
		"v-select": vSelect,
		"case-table": CaseTable
	}
})
export default class ResultsDisplay extends Vue {
	@Prop()
	public searchTerm!: string;

	@Prop()
	public searchType!: string;

	@Prop()
	public perPage!: number;

	@Prop({ default: false })
	public hideFilters!: boolean;

	@Prop()
	public defaultColumns!: string[];

	@Prop()
	public filterColumnGroups!: any[];

	public results: PagedData<any> | null = null;

	public activeColumns: any[] = [];

	public appliedSearchClauses: SearchClause[] = [];
	public filters: AdvancedSearchFilter[] = [];

	private defaultSortBy = "created"
	private paginationItems = [20, 50, 100];
	private itemsPerPage = this.perPage;

	columnFilter: string = "";
	currentPage: number = 1;

	public get filteredFieldColumnGroups (): any[][] {
		if (!this.columnFilter) {
			return this.filterColumnGroups
		}

		const lowerFilter = this.columnFilter.toLowerCase()
		return this.filterColumnGroups.map(g => g.filter((c: any) => c.label.toLowerCase().indexOf(lowerFilter) > -1))
	}

	private get searchingForUsers (): boolean {
		return this.searchType === "users"
	}

	@Watch("currentPage")
	onCurrentPageChanged () {
		this.search()
	}

	public async mounted () {
		// const defaultColumns = ["caseNumber", "title", "templateTitle", "created", "lastUpdated", "statusTitle", "assigneeId"];
		if (this.defaultColumns) {
			this.activeColumns = this.filterColumnGroups[0].filter((c: any) => this.defaultColumns.indexOf(c.key) > -1)
		} else {
			this.activeColumns = []
		}
	}

	public async search () {
		this.results = await api.searchAdvanced(this.searchType,
			this.searchTerm,
			this.appliedSearchClauses,
			this.currentPage)
	}

	/**
	 * If a column is currently in active columns
	 */
	public hasColumn (columnKey: string): boolean {
		return !!this.activeColumns.find(c => c.key === columnKey)
	}

	/**
	 * Toggles a column into the active columns list
	 */
	public toggleColumn (column: any) {
		if (this.hasColumn(column.key)) {
			this.activeColumns.removeWhere(c => c.key === column.key)
		} else {
			this.activeColumns.push(column)
		}
	}

	/**
	 * Get field Label value for field ID
	 */
	public getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	public addFilterForColumn (column: CaseColumn) {
		const columnOperators = column.operators ? column.operators : operatorsByFieldType[column.dataType]
		const firstOperator = columnOperators && columnOperators.length > 0
			? operatorLabels.find(o => o.operator === columnOperators[0])
			: null

		this.filters.push({
			key: column.key,
			columnLabel: column.label,
			columnDataType: column.dataType,
			columnValues: column.columnValues,
			columnListLabel: column.listLabel,
			filterKey: column.filterKey,
			columnDataSource: column.dataSourceId,
			operator: firstOperator!.operator,
			operatorLabel: firstOperator!.label,
			dynamicValue: false,
			value: "",
			label: null,
			column: column
		})
	}

	public removeFilter (filter: any) {
		this.filters.splice(this.filters.indexOf(filter), 1)
	}

	public openCasePreview (caseDetails: any) {
		this.$emit("preview", caseDetails)
	}

	public userPictureUrl (userId: string): string {
		return api.pictureUrl(userId)
	}

	public applyFilters () {
		this.appliedSearchClauses = this.filters.map((filter: AdvancedSearchFilter) => {
			return {
				id: "",
				field: filter.key,
				label: filter.columnLabel,
				dataSourceId: "",
				fieldDataType: filter.column ? filter.column.fieldDataType! : null,
				dynamicField: filter.column ? filter.column.dynamicField : null,
				operator: filter.operator,
				value: filter.value,
				dynamicValue: filter.dynamicValue,
				valueLabel: "",
				definitionId: "",
				definitionField: "",
				definitionFieldLabel: "",
				definitionFieldDataType: 0
			} as SearchClause
		})

		this.search()
	}
}

